/**
 * API Key management component for user profile.
 * Allows generating, viewing, copying, and revoking API keys.
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RegenerateIcon,
  Delete as DeleteIcon,
  VpnKey as KeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, type ApiKeyInfo, type GenerateApiKeyRequest } from '@/api/user';

const AVAILABLE_SCOPES = ['READ', 'WRITE'] as const;

export const ApiKeyManagement: React.FC = () => {
  const queryClient = useQueryClient();

  const [rawKey, setRawKey] = useState<string | null>(null);
  const [showRawKey, setShowRawKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [generateName, setGenerateName] = useState('Default');
  const [generateScopes, setGenerateScopes] = useState<string[]>(['READ', 'WRITE']);
  const [error, setError] = useState<string | null>(null);

  const {
    data: apiKey,
    isLoading,
  } = useQuery({
    queryKey: ['api-key'],
    queryFn: userApi.getApiKey,
  });

  const generateMutation = useMutation({
    mutationFn: (data: GenerateApiKeyRequest) => userApi.generateApiKey(data),
    onSuccess: (result: ApiKeyInfo) => {
      queryClient.invalidateQueries({ queryKey: ['api-key'] });
      setRawKey(result.raw_key || null);
      setShowRawKey(true);
      setGenerateDialogOpen(false);
      setError(null);
    },
    onError: (err: any) => {
      setError(
        err.response?.data?.error_messages?.[0] ||
        err.response?.data?.message ||
        'Failed to generate API key'
      );
    },
  });

  const revokeMutation = useMutation({
    mutationFn: userApi.revokeApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-key'] });
      setRawKey(null);
      setRevokeDialogOpen(false);
      setError(null);
    },
    onError: (err: any) => {
      setError(
        err.response?.data?.error_messages?.[0] ||
        err.response?.data?.message ||
        'Failed to revoke API key'
      );
    },
  });

  const handleCopy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  }, []);

  const handleGenerateConfirm = useCallback(() => {
    generateMutation.mutate({
      name: generateName.trim() || 'Default',
      scopes: generateScopes.length > 0 ? generateScopes : ['READ', 'WRITE'],
    });
  }, [generateMutation, generateName, generateScopes]);

  const handleOpenGenerateDialog = useCallback(() => {
    setGenerateName('Default');
    setGenerateScopes(['READ', 'WRITE']);
    setGenerateDialogOpen(true);
  }, []);

  const formatDate = useCallback((dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ mt: 1 }}>
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        API Key
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Use an API key to authenticate programmatic requests to the Unblockly API.
        Each account can have one active key at a time.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Just-generated key banner */}
      {rawKey && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => handleCopy(rawKey)}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Copy this key now — you won't be able to see it again.
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={showRawKey ? rawKey : '•'.repeat(rawKey.length)}
            slotProps={{
              input: {
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowRawKey((v) => !v)}
                    >
                      {showRawKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Alert>
      )}

      {apiKey ? (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <KeyIcon color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {apiKey.name}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Key Prefix
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {apiKey.prefix}••••••••••••
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body2">
                {formatDate(apiKey.created_at)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Last Used
              </Typography>
              <Typography variant="body2">
                {formatDate(apiKey.last_used_at)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Scopes
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                {(apiKey.scopes || []).map((scope) => (
                  <Chip
                    key={scope}
                    label={scope}
                    size="small"
                    color={scope === 'WRITE' ? 'warning' : 'info'}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<RegenerateIcon />}
              onClick={handleOpenGenerateDialog}
            >
              Regenerate
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setRevokeDialogOpen(true)}
            >
              Revoke
            </Button>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 4,
            textAlign: 'center',
          }}
        >
          <KeyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            No API key generated yet
          </Typography>
          <Button
            variant="contained"
            startIcon={<KeyIcon />}
            onClick={handleOpenGenerateDialog}
          >
            Generate API Key
          </Button>
        </Box>
      )}

      {/* Generate Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {apiKey ? 'Regenerate API Key' : 'Generate API Key'}
        </DialogTitle>
        <DialogContent>
          {apiKey && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will revoke your existing key. Any integrations using the old
              key will stop working immediately.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Key Name"
            value={generateName}
            onChange={(e) => setGenerateName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            helperText="A label to help you identify this key"
          />
          <FormControl fullWidth>
            <InputLabel>Scopes</InputLabel>
            <Select
              multiple
              value={generateScopes}
              onChange={(e) => setGenerateScopes(e.target.value as string[])}
              label="Scopes"
              renderValue={(selected) => (selected as string[]).join(', ')}
            >
              {AVAILABLE_SCOPES.map((scope) => (
                <MenuItem key={scope} value={scope}>
                  <Checkbox checked={generateScopes.includes(scope)} />
                  <ListItemText
                    primary={scope}
                    secondary={
                      scope === 'READ'
                        ? 'Read-only access to resources'
                        : 'Create, update, and delete resources'
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGenerateConfirm}
            disabled={generateMutation.isPending || generateScopes.length === 0}
            startIcon={
              generateMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <KeyIcon />
              )
            }
          >
            {generateMutation.isPending ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => setRevokeDialogOpen(false)}
      >
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke this API key? Any integrations using
            this key will immediately lose access. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => revokeMutation.mutate()}
            disabled={revokeMutation.isPending}
            startIcon={
              revokeMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {revokeMutation.isPending ? 'Revoking...' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
